import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Plus,
  Copy,
  ExternalLink,
  Tv,
  QrCode,
  Play,
  Lock,
  Ban,
  Trash2,
  Mail,
  Building,
  Building2,
  UserCog,
  User,
  ShieldAlert,
  X,
  Pencil,
  Timer,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import EmployeesPicker from "@/components/EmployeesPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminShare } from "@/api/share";
import { api, formatApiError } from "@/api/client";

const SCOPE_OPTIONS = [
  { value: "corporate", icon: Building, color: "#9ECE6A" },
  { value: "department", icon: Building2, color: "#7DCFFF" },
  { value: "supervisor", icon: UserCog, color: "#BB9AF7" },
  { value: "employee", icon: User, color: "#F7C56D" },
];

const ALL_VIEWS = [
  "dashboard",
  "ganador",
  "ranking",
  "comparativo",
  "tendencia",
  "distribucion",
  "heatmap",
  "tabla",
  "efficiency",
  "3d",
];

const STATUS_COLORS = {
  active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  revoked: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  expired: "bg-amber-500/15 text-amber-500 border-amber-500/30",
};

function publicUrl(token, extra = "") {
  const base = window.location.origin;
  return `${base}/share/${token}${extra}`;
}

function truncateMid(str, max = 50) {
  if (!str || str.length <= max) return str;
  return `${str.slice(0, max / 2)}…${str.slice(-(max / 2 - 1))}`;
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------
function CreateForm({ onCreated, departments, supervisors }) {
  const { t } = useTranslation();
  const [scope, setScope] = useState("corporate");
  const [label, setLabel] = useState("");
  const [deptIds, setDeptIds] = useState([]);
  const [empIds, setEmpIds] = useState([]);
  const [supervisorId, setSupervisorId] = useState("");
  const [preset, setPreset] = useState("year");
  const [pin, setPin] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("");
  const [views, setViews] = useState(ALL_VIEWS);
  const [defaultView, setDefaultView] = useState("dashboard");
  const [defaultMode, setDefaultMode] = useState("normal");
  const [defaultInterval, setDefaultInterval] = useState(20);
  const [submitting, setSubmitting] = useState(false);

  const toggleView = (v) =>
    setViews((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const create = async () => {
    setSubmitting(true);
    try {
      const scope_value = {};
      if (scope === "department") scope_value.department_ids = deptIds;
      if (scope === "employee") scope_value.employee_ids = empIds;
      if (scope === "supervisor") scope_value.supervisor_user_id = supervisorId;
      const payload = {
        label: label || null,
        scope,
        scope_value,
        filters: { preset: preset || null },
        views_to_include: views,
        default_view: views.includes(defaultView) ? defaultView : views[0],
        default_mode: defaultMode,
        default_interval: Number(defaultInterval) || 20,
        pin: pin || null,
        expires_in_days: expiresInDays ? Number(expiresInDays) : null,
      };
      const r = await adminShare.create(payload);
      toast.success(t("share.createdToast"));
      onCreated?.(r.data);
      // Reset
      setLabel("");
      setPin("");
      setExpiresInDays("");
      setDeptIds([]);
      setEmpIds([]);
      setSupervisorId("");
      setDefaultMode("normal");
      setDefaultInterval(20);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="share-create-form">
      <CardHeader>
        <CardTitle className="text-base">{t("share.createTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Scope picker */}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {SCOPE_OPTIONS.map((s) => {
            const Icon = s.icon;
            const active = scope === s.value;
            return (
              <button
                key={s.value}
                onClick={() => setScope(s.value)}
                className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                    : "border-border bg-card hover:border-primary/40"
                }`}
                data-testid={`scope-card-${s.value}`}
                aria-pressed={active}
              >
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white"
                  style={{ background: s.color }}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {t(`share.scope.${s.value}.title`)}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {t(`share.scope.${s.value}.desc`)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Scope-specific selectors */}
        {scope === "department" && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.departments")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {departments.map((d) => {
                const checked = deptIds.includes(d.id);
                return (
                  <button
                    key={d.id}
                    onClick={() =>
                      setDeptIds((s) =>
                        checked ? s.filter((x) => x !== d.id) : [...s, d.id]
                      )
                    }
                    className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      checked
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:border-primary/40"
                    }`}
                    data-testid={`share-dept-${d.id}`}
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ background: d.color || "#7DCFFF" }}
                    />
                    {d.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {scope === "employee" && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.employees")}</Label>
            <EmployeesPicker
              value={empIds}
              onChange={setEmpIds}
              placeholder={t("share.fields.employees")}
              testId="share-emp-picker"
            />
          </div>
        )}
        {scope === "supervisor" && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.supervisor")}</Label>
            <Select value={supervisorId} onValueChange={setSupervisorId}>
              <SelectTrigger className="h-9" data-testid="share-supervisor-select">
                <SelectValue placeholder={t("share.fields.supervisorPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {supervisors.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name} · {u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Filters + meta */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.preset")}</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["today", "yesterday", "week", "month", "year"].map((p) => (
                  <SelectItem key={p} value={p}>
                    {t(`filters.${p}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.label")}</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t("share.fields.labelPlaceholder")}
              className="h-9"
              data-testid="share-label-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.expiresInDays")}</Label>
            <Input
              type="number"
              min="1"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              placeholder={t("share.fields.never")}
              className="h-9"
              data-testid="share-expires-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.pin")}</Label>
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={t("share.fields.pinPlaceholder")}
              className="h-9"
              data-testid="share-pin-input"
            />
          </div>
        </div>

        {/* Views to include */}
        <div className="space-y-2">
          <Label className="text-xs">{t("share.fields.viewsToInclude")}</Label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_VIEWS.map((v) => {
              const on = views.includes(v);
              return (
                <button
                  key={v}
                  onClick={() => toggleView(v)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] ${
                    on
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground"
                  }`}
                  data-testid={`share-view-${v}`}
                >
                  {t(`nav.${v === "3d" ? "vista3d" : v}`)}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">{t("share.fields.defaultView")}</Label>
            <Select value={defaultView} onValueChange={setDefaultView}>
              <SelectTrigger className="h-8 w-48 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {views.map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`nav.${v === "3d" ? "vista3d" : v}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DefaultModePicker
          mode={defaultMode}
          onMode={setDefaultMode}
          interval={defaultInterval}
          onInterval={setDefaultInterval}
          testIdPrefix="share-create"
        />

        <Button
          onClick={create}
          disabled={submitting}
          data-testid="share-generate-btn"
          className="w-full sm:w-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("share.generate")}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Default-mode picker (shared by Create + Edit)
// ---------------------------------------------------------------------------
const MODE_OPTIONS = [
  { value: "normal", icon: ExternalLink },
  { value: "tv", icon: Tv },
  { value: "slides", icon: Play },
  { value: "tv_slides", icon: Timer },
];

function DefaultModePicker({ mode, onMode, interval, onInterval, testIdPrefix = "share" }) {
  const { t } = useTranslation();
  const needsInterval = mode === "slides" || mode === "tv_slides";
  return (
    <div className="space-y-3 rounded-lg border border-border/60 bg-muted/30 p-3">
      <div>
        <Label className="text-xs font-semibold">{t("share.fields.defaultMode")}</Label>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {t("share.fields.defaultModeHint")}
        </div>
      </div>
      <div
        className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4"
        role="radiogroup"
        data-testid={`${testIdPrefix}-default-mode-group`}
      >
        {MODE_OPTIONS.map((m) => {
          const Icon = m.icon;
          const active = mode === m.value;
          return (
            <button
              key={m.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onMode(m.value)}
              className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${
                active
                  ? "border-primary bg-primary/10 ring-1 ring-primary"
                  : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`${testIdPrefix}-mode-${m.value}`}
            >
              <div
                className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full ${
                  active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold">
                  {t(`share.modes.${m.value}`)}
                </div>
                <div className="text-[10px] leading-snug text-muted-foreground">
                  {t(`share.modes.${m.value}Desc`)}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {needsInterval && (
        <div className="grid gap-2 sm:max-w-xs">
          <Label className="text-xs">{t("share.fields.defaultInterval")}</Label>
          <Input
            type="number"
            min={3}
            max={600}
            value={interval}
            onChange={(e) => onInterval(e.target.value)}
            className="h-9"
            data-testid={`${testIdPrefix}-default-interval`}
          />
          <div className="text-[10px] text-muted-foreground">
            {t("share.fields.defaultIntervalHint")}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Link card
// ---------------------------------------------------------------------------
function LinkCard({ link, onCopy, onRevoke, onDelete, onOpenQr, onEdit }) {
  const { t } = useTranslation();
  const url = publicUrl(link.token);
  const scopeOpt = SCOPE_OPTIONS.find((s) => s.value === link.scope);
  const ScopeIcon = scopeOpt?.icon || Building;
  const statusCls = STATUS_COLORS[link.status] || STATUS_COLORS.active;
  const mode = link.default_mode || "normal";
  const interval = link.default_interval || 20;
  const ModeIcon =
    mode === "tv" ? Tv : mode === "slides" ? Play : mode === "tv_slides" ? Timer : null;

  const openLink = (extra) => window.open(publicUrl(link.token, extra), "_blank");
  const openMail = () => {
    const subject = encodeURIComponent(t("share.mailSubject"));
    const body = encodeURIComponent(`${t("share.mailBody")}\n\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Card
      data-testid={`share-link-row-${link.id}`}
      data-status={link.status}
      className={link.status !== "active" ? "opacity-70" : ""}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full text-white"
              style={{ background: scopeOpt?.color || "#7DCFFF" }}
            >
              <ScopeIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">
                {link.label || `[${t(`share.scope.${link.scope}.title`)}]`}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">
                {truncateMid(url, 50)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge
              variant="outline"
              className={`border text-[10px] uppercase ${statusCls}`}
            >
              {t(`share.status.${link.status}`)}
            </Badge>
            {link.requires_pin && (
              <Badge variant="outline" className="text-[10px]">
                <Lock className="mr-1 h-3 w-3" /> PIN
              </Badge>
            )}
            {ModeIcon && (
              <Badge
                variant="outline"
                className="border-primary/40 text-[10px] text-primary"
                data-testid={`share-default-mode-badge-${link.id}`}
                title={`${t(`share.modes.${mode}`)}${
                  mode === "slides" || mode === "tv_slides" ? ` · ${interval}s` : ""
                }`}
              >
                <ModeIcon className="mr-1 h-3 w-3" />
                {t(`share.modes.${mode}`)}
                {(mode === "slides" || mode === "tv_slides") && ` · ${interval}s`}
              </Badge>
            )}
          </div>
        </div>

        {/* Filters chips */}
        <div className="flex flex-wrap gap-1">
          {link.filters?.preset && (
            <Badge variant="secondary" className="text-[10px]">
              {t(`filters.${link.filters.preset}`)}
            </Badge>
          )}
          {(link.filters?.transaction_types || []).map((t2) => (
            <Badge key={t2} variant="secondary" className="text-[10px]">
              {t2}
            </Badge>
          ))}
        </div>

        <div className="text-[11px] text-muted-foreground">
          <span data-testid={`share-views-${link.id}`}>
            {t("share.viewsCount", { n: link.view_count })}
          </span>
          {" · "}
          <span>
            {t("share.created")}: {formatDateTime(link.created_at)}
          </span>
          {link.expires_at && (
            <>
              {" · "}
              <span>
                {t("share.expires")}: {formatDateTime(link.expires_at)}
              </span>
            </>
          )}
        </div>

        {link.status === "active" && (
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onCopy(url)}
              className="h-7 gap-1 text-[11px]"
              data-testid={`share-copy-${link.id}`}
            >
              <Copy className="h-3 w-3" /> {t("share.actions.copy")}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openLink("")}
              className="h-7 gap-1 text-[11px]"
              data-testid={`share-open-${link.id}`}
            >
              <ExternalLink className="h-3 w-3" /> {t("share.actions.open")}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openLink("?mode=tv&slides=1")}
              className="h-7 gap-1 text-[11px]"
              data-testid={`share-tv-${link.id}`}
            >
              <Tv className="h-3 w-3" /> {t("share.actions.tv")}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => openLink("?mode=slides&interval=20")}
              className="h-7 gap-1 text-[11px]"
              data-testid={`share-slides-${link.id}`}
            >
              <Play className="h-3 w-3" /> {t("share.actions.slides")}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onOpenQr(link)}
              className="h-7 gap-1 text-[11px]"
              data-testid={`share-qr-${link.id}`}
            >
              <QrCode className="h-3 w-3" /> {t("share.actions.qr")}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={openMail}
              className="h-7 gap-1 text-[11px]"
            >
              <Mail className="h-3 w-3" /> {t("share.actions.email")}
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          {link.status === "active" && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onEdit(link)}
                className="h-7 gap-1 text-[11px]"
                data-testid={`share-edit-${link.id}`}
              >
                <Pencil className="h-3 w-3" /> {t("common.edit")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRevoke(link)}
                className="h-7 gap-1 text-[11px] text-amber-500"
                data-testid={`share-revoke-${link.id}`}
              >
                <Ban className="h-3 w-3" /> {t("share.actions.revoke")}
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(link)}
            className="h-7 gap-1 text-[11px] text-rose-500"
            data-testid={`share-delete-${link.id}`}
          >
            <Trash2 className="h-3 w-3" /> {t("share.actions.delete")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// QR Modal
// ---------------------------------------------------------------------------
function QrModal({ link, onClose }) {
  const { t } = useTranslation();
  if (!link) return null;
  const url = publicUrl(link.token);
  const download = () => {
    const canvas = document.querySelector('[data-testid="share-qr-canvas"]');
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `share-${link.token}.png`;
    a.click();
  };
  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="share-qr-modal">
        <DialogHeader>
          <DialogTitle>{t("share.qr.title")}</DialogTitle>
          <DialogDescription>{t("share.qr.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="rounded-lg bg-white p-3">
            <QRCodeCanvas
              value={url}
              size={240}
              includeMargin
              data-testid="share-qr-canvas"
            />
          </div>
          <div className="break-all text-center font-mono text-[10px] text-muted-foreground">
            {url}
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="share-qr-close">
            {t("common.cancel")}
          </Button>
          <Button onClick={download} data-testid="share-qr-download">
            {t("share.qr.download")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit Modal — quick-edit label, default mode and slide interval.
// ---------------------------------------------------------------------------
function EditModal({ link, onClose, onSaved }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState("normal");
  const [interval, setInterval] = useState(20);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!link) return;
    setLabel(link.label || "");
    setMode(link.default_mode || "normal");
    setInterval(link.default_interval || 20);
  }, [link]);

  if (!link) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await adminShare.patch(link.id, {
        label: label || null,
        default_mode: mode,
        default_interval: Number(interval) || 20,
      });
      toast.success(t("share.edit.saved"));
      onSaved?.(r.data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!link} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-xl" data-testid="share-edit-modal">
        <DialogHeader>
          <DialogTitle>{t("share.edit.title")}</DialogTitle>
          <DialogDescription>{t("share.edit.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("share.fields.label")}</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-9"
              data-testid="share-edit-label-input"
            />
          </div>
          <DefaultModePicker
            mode={mode}
            onMode={setMode}
            interval={interval}
            onInterval={setInterval}
            testIdPrefix="share-edit"
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} data-testid="share-edit-cancel">
            {t("common.cancel")}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="share-edit-save">
            {t("share.edit.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function ShareLinks() {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [departments, setDepartments] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [createdLink, setCreatedLink] = useState(null);
  const [qrLink, setQrLink] = useState(null);
  const [editLink, setEditLink] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(null); // "revoked"|"expired"|"all"

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminShare.list(statusFilter, search);
      setLinks(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    Promise.all([
      api.get("/departments"),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([d, u]) => {
      setDepartments(d.data || []);
      setSupervisors((u.data || []).filter((x) => x.active !== false));
    });
  }, []);

  const filteredLinks = useMemo(() => {
    if (!search) return links;
    const lc = search.toLowerCase();
    return links.filter((l) =>
      (l.label || "").toLowerCase().includes(lc) || l.token.toLowerCase().includes(lc)
    );
  }, [links, search]);

  const counts = useMemo(() => {
    const c = { active: 0, revoked: 0, expired: 0 };
    links.forEach((l) => {
      c[l.status] = (c[l.status] || 0) + 1;
    });
    return c;
  }, [links]);

  const onCopy = async (url) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Older browsers / no clipboard permission — fall back to a hidden textarea.
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(t("share.copied"));
    } catch (err) {
      toast.error(formatApiError(err) || t("share.copied"));
    }
  };

  const doRevoke = async () => {
    if (!confirmRevoke) return;
    try {
      await adminShare.revoke(confirmRevoke.id);
      toast.success(t("share.revoked"));
      setConfirmRevoke(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await adminShare.remove(confirmDelete.id);
      toast.success(t("share.deleted"));
      setConfirmDelete(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const doBulk = async () => {
    if (!confirmBulk) return;
    try {
      const r = await adminShare.bulkCleanup(confirmBulk);
      toast.success(t("share.bulkCleanupDone", { n: r.data.deleted }));
      setConfirmBulk(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="share-links-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t("share.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("share.subtitle")}</p>
        </div>
        <Badge variant="outline" className="border-primary/40 text-primary">
          ADMIN
        </Badge>
      </div>

      {/* Security banner */}
      <div
        className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
        data-testid="share-security-banner"
      >
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-none text-amber-500" />
        <span className="text-amber-900 dark:text-amber-200">
          {t("share.securityBanner")}
        </span>
      </div>

      <CreateForm
        departments={departments}
        supervisors={supervisors}
        onCreated={(link) => {
          setCreatedLink(link);
          refresh();
        }}
      />

      {/* List header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="share-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["all", "active", "revoked", "expired"].map((s) => (
                <SelectItem key={s} value={s}>
                  {t(`share.status.${s}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("common.search")}
            className="h-9 w-56"
          />
          <span className="text-xs text-muted-foreground">
            {t("share.count", { n: filteredLinks.length, total: links.length })}
          </span>
        </div>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmBulk("revoked")}
            disabled={!counts.revoked}
            className="h-8 text-[11px]"
            data-testid="share-bulk-revoked"
          >
            {t("share.bulk.revoked", { n: counts.revoked })}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmBulk("expired")}
            disabled={!counts.expired}
            className="h-8 text-[11px]"
            data-testid="share-bulk-expired"
          >
            {t("share.bulk.expired", { n: counts.expired })}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmBulk("all")}
            disabled={!links.length}
            className="h-8 text-[11px] text-rose-500"
            data-testid="share-bulk-all"
          >
            {t("share.bulk.all")}
          </Button>
        </div>
      </div>

      {/* Links list */}
      <div className="grid gap-3 lg:grid-cols-2">
        {loading ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t("common.loading")}
            </CardContent>
          </Card>
        ) : filteredLinks.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">
              {t("common.no_data")}
            </CardContent>
          </Card>
        ) : (
          filteredLinks.map((link) => (
            <LinkCard
              key={link.id}
              link={link}
              onCopy={onCopy}
              onRevoke={setConfirmRevoke}
              onDelete={setConfirmDelete}
              onOpenQr={setQrLink}
              onEdit={setEditLink}
            />
          ))
        )}
      </div>

      {/* Created link modal */}
      <Dialog open={!!createdLink} onOpenChange={(o) => !o && setCreatedLink(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="share-created-modal">
          <DialogHeader>
            <DialogTitle>{t("share.createdTitle")}</DialogTitle>
            <DialogDescription>{t("share.createdDesc")}</DialogDescription>
          </DialogHeader>
          {createdLink && (
            <div className="space-y-3">
              <div className="break-all rounded border border-border bg-muted/40 p-3 font-mono text-xs">
                {publicUrl(createdLink.token)}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    onCopy(publicUrl(createdLink.token));
                  }}
                  data-testid="share-created-copy"
                >
                  <Copy className="mr-1 h-3.5 w-3.5" />
                  {t("share.actions.copy")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.open(publicUrl(createdLink.token), "_blank")}
                >
                  <ExternalLink className="mr-1 h-3.5 w-3.5" />
                  {t("share.actions.open")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <QrModal link={qrLink} onClose={() => setQrLink(null)} />

      <EditModal
        link={editLink}
        onClose={() => setEditLink(null)}
        onSaved={() => refresh()}
      />

      {/* Confirm revoke */}
      <AlertDialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("share.confirmRevoke")}</AlertDialogTitle>
            <AlertDialogDescription>{t("share.confirmRevokeDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doRevoke} data-testid="share-confirm-revoke-btn">
              {t("share.actions.revoke")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("share.confirmDelete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("share.confirmDeleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-destructive text-destructive-foreground"
              data-testid="share-confirm-delete-btn"
            >
              {t("share.actions.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm bulk cleanup */}
      <AlertDialog open={!!confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`share.bulkConfirm.${confirmBulk || "revoked"}`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(`share.bulkConfirmDesc.${confirmBulk || "revoked"}`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulk}
              className="bg-destructive text-destructive-foreground"
              data-testid="share-confirm-bulk-btn"
            >
              {t("common.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
